/**
 * 조직 멤버의 표시 종류(company-rbac P7-b-2, 2026-09-25) — 옛 `MemberRole = 'OWNER' | 'SAFETY_MANAGER'`(서버 `CompanyMember.role`
 * enum)는 컬럼과 함께 지워졌다. 서버가 배정 축에서 판정해 싣는 두 성질(`isOwner`·`countsAsMember`)만 읽는다.
 * 강제 지정·복원·생성이 보내는 것은 **시스템 역할 key**(`roleKey`)이고 후보는 `/admin/rbac/roles`(`useSystemRoleOptions`)에서 온다.
 */
import { useQuery } from '@tanstack/react-query';
import {
  memberKindOf,
  MEMBER_KIND_LABELS,
  OWNER_LABEL,
  MEMBER_LABEL,
  OWNER_ROLE_KEY,
} from '@safetyhub/shared';
import { companyRbacApi } from './company-rbac';

// 판정(`memberKindOf`)과 소유자·구성원 라벨은 **shared가 소유한다**(`constants/member-kind.ts`) — 종전엔
// web과 admin이 각자 같은 판정을 들고 있어 두 화면이 갈릴 수 있었다. 기존 import 경로는 유지한다.
export { OWNER_LABEL, MEMBER_LABEL, OWNER_ROLE_KEY };

/** 외부 인원(배정 0) 라벨 — **admin의 문구**다. web은 같은 종류를 「외부 인원」이라 부른다(제품 결정 전까지 각자). */
export const EXTERNAL_LABEL = '외부 연락처';

export interface MemberKindLike {
  isOwner?: boolean | null;
  countsAsMember?: boolean | null;
}

export function getMemberKindLabel(m: MemberKindLike): string {
  const kind = memberKindOf(m);
  return kind ? MEMBER_KIND_LABELS[kind] : EXTERNAL_LABEL;
}

/**
 * 감사 로그(멤버십 히스토리)에 남은 역할 문자열의 라벨 — 옛 로그는 enum(`OWNER`·`SAFETY_MANAGER`), P7-b-2 이후 로그는
 * 시스템 역할 key(`owner`·`safety-manager`…)를 싣는다. 둘 다 사람이 읽을 수 있게만 하면 된다(판정에 쓰지 않는다).
 */
export function legacyRoleLabel(role: string): string {
  const LEGACY: Record<string, string> = { OWNER: OWNER_LABEL, SAFETY_MANAGER: '안전관리자', owner: OWNER_LABEL };
  return LEGACY[role] ?? role;
}

/**
 * 시스템 역할 key 기본값 — admin 강제 지정 폼의 초기 선택. 시드(`SYSTEM_ROLE_SEEDS`)의 key와 같다(옛 폼 기본값
 * SAFETY_MANAGER / 새 조직 OWNER의 대응). 후보 목록에 없으면 첫 후보로 떨어진다(`useSystemRoleOptions`).
 */
export const DEFAULT_MEMBER_ROLE_KEY = 'safety-manager';

export interface SystemRoleOption {
  key: string;
  name: string;
  isOwner: boolean;
}

/** 활성(미보관) 시스템 역할 후보 — 강제 지정·복원·생성 폼의 select 옵션 */
export function useSystemRoleOptions(enabled = true) {
  const query = useQuery({
    queryKey: ['admin', 'rbac', 'roles', 'options'],
    queryFn: () => companyRbacApi.listRoles(),
    enabled,
    staleTime: 60_000,
  });
  const options: SystemRoleOption[] = (query.data ?? [])
    .filter((r) => r.archivedAt === null)
    .map((r) => ({ key: r.key, name: r.name, isOwner: r.isOwner }));
  return { options, isLoading: query.isLoading };
}
